/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Inject, Input, OnInit, signal} from '@angular/core';
import * as _ from 'lodash';

import template from './third-party-audience-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';
import {TargetingHelper} from '../targeting-helper';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {ThirdPartyAudienceApiService} from '../../third-party-audience/third-party-audience-api.service';

import {TARGETING_IMPORTS} from '../targeting-imports';
import {TargetingTemplateComponent} from './targeting-template.component';

@Component({
  imports: [...TARGETING_IMPORTS, TargetingTemplateComponent],
  selector: 'adn-third-party-audience-targeting',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThirdPartyAudienceTargetingComponent implements OnInit {
  @Input() model: any[];
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.thirdPartyAudience;
  @Input() underModel: any;
  @Input() modelLineItem: any;
  @Input() pageType: string;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  private readonly inputsState = signal<any[]>([]);
  get inputs(): any[] { return this.inputsState(); }
  set inputs(value: any[]) { this.inputsState.set(value); }
  templateState: any = {
    loaded: true,
    complexInput: false
  };
  private readonly listState = signal<any>({searchResults: []});
  get list(): any { return this.listState(); }
  set list(value: any) { this.listState.set(value); }
  search: any = {term: ''};
  searchMeta: any = {
    pageSize: 50,
    currentPage: 1,
    resolved: true
  };

  manualClose = false;
  showFeeInfo = false;
  disableButtons = false;
  fee: any;

  private initHasNothing = true;
  private queryDone = false;

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      const summary = _.flatten(_.map(this.model, (item: any) => {
        return _.map(item.thirdPartyAudiences, 'name');
      })).join(', ');
      return {
        vm: _.cloneDeep(this.model),
        model: _.cloneDeep(this.model),
        constant: ADN_TARGETING_TYPES.thirdPartyAudience,
        summary
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(
    private readonly audienceApi: ThirdPartyAudienceApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly adnLog: AdnLogService
  ) {
  }

  ngOnInit(): void {
    this.ensureModel();
    this.fee = _.get(this.LocalNetworkProfile.getAudienceFees(), 'NORDIC_DATA_RESOURCES');
    this.templateState.complexInput = this.init();
    this.initInputs();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.thirdPartyAudience.widget, () => this.onTabSelect(), copyTemplate);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
  }

  get filteredSearchResults(): any[] {
    const term = (this.search.term || '').toLowerCase();
    const results = _.get(this.list, ['searchResults'], []);
    if (!term) {
      return results;
    }
    return _.filter(results, (segment: any) => {
      return [segment.name, segment.description, segment.externalReference, segment.provider]
        .some((val) => String(val || '').toLowerCase().indexOf(term) > -1);
    });
  }

  simpleOutput(audience: any): any {
    return _.get(audience, ['thirdPartyAudiences', 'length'])
      ? audience.thirdPartyAudiences[0]
      : audience.notThirdPartyAudiences[0];
  }

  select(audience: any): void {
    this.showFeeInfo = this.initHasNothing;
    this.addEntry(audience);
    audience.selected = true;
  }

  selectOrNot(audience: any): void {
    this.showFeeInfo = this.initHasNothing;
    this.addNotEntry(audience);
    audience.selected = true;
  }

  removeFromTarget(audience?: any): void {
    audience = audience || {};
    audience.selected = false;

    let found = false;
    _.forEach(this.inputs, (input: any) => {
      ['thirdPartyAudiences'].forEach((field) => {
        input[field] = _.filter(input[field], (aud: any) => {
          if (!found && ((!audience.id && (!aud || !aud.id)) || _.get(aud, 'id') === _.get(audience, 'id'))) {
            found = true;
            return false;
          }
          return true;
        });
      });
    });
    this.inputs = _.filter(this.inputs, (input: any) => {
      return input.thirdPartyAudiences.length > 0;
    });
    const listMatch = _.find(_.get(this.list, ['searchResults'], []), ['id', audience.id]);
    if (listMatch) {
      listMatch.selected = false;
    }
  }

  addToRow(index?: number, entry?: any): void {
    const theIndex = _.isFinite(index) ? index as number : this.inputs.length - 1;
    this.inputs[theIndex].thirdPartyAudiences.push(entry || {});
  }

  addNotToRow(index?: number, entry?: any): void {
    const theIndex = _.isFinite(index) ? index as number : this.inputs.length - 1;
    this.inputs[theIndex].notThirdPartyAudiences = this.inputs[theIndex].notThirdPartyAudiences || [];
    this.inputs[theIndex].notThirdPartyAudiences.push(entry || {});
  }

  removeFromRow(parentIndex: number, index: number): void {
    this.inputs[parentIndex].thirdPartyAudiences.splice(index, 1);
    if (!this.inputs[parentIndex].thirdPartyAudiences.length && !this.inputs[parentIndex].notThirdPartyAudiences?.length) {
      this.inputs.splice(parentIndex, 1);
    }
  }

  removeNotFromRow(parentIndex: number, index: number): void {
    this.inputs[parentIndex].notThirdPartyAudiences.splice(index, 1);
    if (!this.inputs[parentIndex].thirdPartyAudiences.length && !this.inputs[parentIndex].notThirdPartyAudiences?.length) {
      this.inputs.splice(parentIndex, 1);
    }
  }

  onRemoveFromTarget(event: Event, audience: any): void {
    event.stopPropagation();
    event.preventDefault();
    this.removeFromTarget(audience);
  }

  private onTabSelect(): void {
    if (this.queryDone) {
      return;
    }
    this.queryDone = false;
    this.loadAudienceList();

    const audienceIds = this.getAudienceIds();
    this.queryResource(_.map(audienceIds), (page: any) => {
      const selAudiences = page.results;

      _.forEach(this.model, (modelEntry: any) => {
        _.forEach(
          _.flatMap([modelEntry.thirdPartyAudiences, modelEntry.notThirdPartyAudiences], (audienceArray: any) => {
            return audienceArray || [];
          }),
          (audience: any) => {
            if (!audience || !audience.id) {
              return;
            }
            const foundAudience = _.find(selAudiences, (s: any) => s.id === audience.id);
            if (foundAudience) {
              audience.selected = true;
              audience.className = 'label-default';
              audience.objectState = foundAudience.objectState;
              audience.description = foundAudience.description;
              audience.externalReference = foundAudience.externalReference;
              audience.provider = foundAudience.provider;
            } else {
              audience.className = 'label-danger';
              audience.translation = 'Unknown';
              audience.objectState = 'UNINDEXED';
              audience.externalReference = '-';
              audience.provider = 'UNKNOWN';
              this.adnLog.warn("Couldn't find selected audience in list", audience);
            }
          }
        );
      });
      const inputs = _.cloneDeep(this.model);
      this.inputs = inputs;
      this.queryDone = true;
    });
  }

  private loadAudienceList(): void {
    this.searchMeta.resolved = false;

    this.queryResource({pageSize: 4000}, (page: any) => {
      const list = {
        searchResults: _.map(page.results, (entry: any) => {
          const data: any = _.pick(entry, ['id', 'provider', 'externalReference', 'name', 'description', 'objectState']);
          const audienceIds = this.getAudienceIds() || [];
          if (audienceIds.indexOf(data.id) > -1) {
            data.selected = true;
          }
          return data;
        })
      };
      this.list = list;
      this.searchMeta.resolved = true;
    });
  }

  private addEntry(entry: any): void {
    const us = _.cloneDeep(entry);
    this.inputs.push({thirdPartyAudiences: [us], notThirdPartyAudiences: []});
  }

  private addNotEntry(entry: any): void {
    const us = _.cloneDeep(entry);
    this.inputs.push({thirdPartyAudiences: [], notThirdPartyAudiences: [us]});
  }

  private getAudienceIds(): any[] {
    return _.flatMap(this.inputs, (row: any) => {
      return _.flatMap([row.thirdPartyAudiences, row.notThirdPartyAudiences], (audienceArray: any) => {
        return _.flatMap(audienceArray || [], (audience: any) => audience?.id ?? []);
      });
    });
  }

  private ensureModel(): void {
    if (!this.model) {
      return;
    }
    if (!_.isArray(this.model)) {
      return;
    }
  }

  private initInputs(): void {
    this.inputs = _.cloneDeep(this.model || []);
  }

  private init(): boolean {
    this.initHasNothing = _.get(this.modelLineItem, 'enabledThirdPartyProviders', []).length === 0;
    this.disableButtons = this.initHasNothing &&
      (this.modelLineItem?.executionState === 'RUNNING' || this.modelLineItem?.executionState === 'ENDED');
    return TargetingHelper.isComplexThirdPartyAudience(this.model);
  }

  private prepareForSave(): void {
    if (!this.model) {
      return;
    }
    this.model.length = 0;
    _.forEach(this.inputs, (entry: any) => {
      if (this.templateState.complexInput) {
        this.model.push(entry);
      } else {
        this.model.push(entry.thirdPartyAudiences.length
          ? {thirdPartyAudiences: [entry.thirdPartyAudiences[0]]}
          : {notThirdPartyAudiences: [entry.notThirdPartyAudiences[0]]});
      }
    });
    this.templateState.complexInput = this.init();
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.model.length = 0;
    Array.prototype.push.apply(this.model, copied);
    this.inputs = _.cloneDeep(this.model);
    this.templateState.complexInput = this.init();
  }

  private queryResource(params: any, callback: (page: any) => void): void {
    this.audienceApi.searchForTargeting(params).subscribe((page) => {
      callback(page);
    });
  }
}
